import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { useAdminTeams, useCreateAdminTeam, useSuspendTeam, useReactivateTeam } from "@/lib/api/teams.queries";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { Table, THead, TH, TBody, TR, TD, EmptyState } from "@/components/ui/Table";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { StatusBadge } from "@/components/ui/Badge";
import { Dialog } from "@/components/ui/Dialog";
import { formatDate, maskPhone } from "@/lib/utils";

function normalizeOwnerPhone(raw: string): string {
  return raw.replace(/\s+/g, "").replace(/^\+?57/, "");
}

export function TeamsList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [suspendTarget, setSuspendTarget] = useState<{ id: string; name: string } | null>(null);
  const [suspendReason, setSuspendReason] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createPhone, setCreatePhone] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const query = useAdminTeams({
    q: search || undefined,
    status: statusFilter || undefined,
    page,
    pageSize,
  });

  const createMutation = useCreateAdminTeam();
  const suspendMutation = useSuspendTeam();
  const reactivateMutation = useReactivateTeam();

  function onSearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  }

  function closeCreateDialog() {
    setCreateOpen(false);
    setCreateName("");
    setCreatePhone("");
    setCreateError(null);
  }

  async function onCreate() {
    const name = createName.trim();
    const ownerPhone = normalizeOwnerPhone(createPhone);
    if (name.length < 2) {
      setCreateError("El nombre debe tener al menos 2 caracteres.");
      return;
    }
    if (!/^3\d{9}$/.test(ownerPhone)) {
      setCreateError("Celular inválido. Usa un celular colombiano de 10 dígitos.");
      return;
    }
    setCreateError(null);
    try {
      const team = await createMutation.mutateAsync({ name, ownerPhone });
      toast.success(`Equipo "${team.name}" creado.`);
      closeCreateDialog();
      navigate(`/teams/${team.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === "INVALID_PHONE") {
        setCreateError("Celular inválido. Usa un celular colombiano de 10 dígitos.");
      } else {
        setCreateError(err instanceof Error ? err.message : "Error al crear equipo.");
      }
    }
  }

  async function onSuspend() {
    if (!suspendTarget) return;
    try {
      await suspendMutation.mutateAsync({ teamId: suspendTarget.id, reason: suspendReason });
      toast.success(`Equipo "${suspendTarget.name}" suspendido.`);
      setSuspendTarget(null);
      setSuspendReason("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al suspender equipo.");
    }
  }

  async function onReactivate(teamId: string, teamName: string) {
    try {
      await reactivateMutation.mutateAsync(teamId);
      toast.success(`Equipo "${teamName}" reactivado.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al reactivar equipo.");
    }
  }

  const total = query.data?.pagination.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Equipos</h1>
          <p className="text-sm text-slate-500 mt-1">
            Todos los equipos de contadores registrados en la plataforma.
          </p>
        </div>
        <Button variant="primary" size="md" onClick={() => setCreateOpen(true)}>
          Crear equipo
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <form onSubmit={onSearch} className="flex items-center gap-2 flex-1 min-w-0 max-w-md">
          <div className="relative flex-1">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar por nombre o slug"
              className="pl-8"
            />
          </div>
          <Button variant="outline" size="md" type="submit">
            Buscar
          </Button>
        </form>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <option value="">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="suspended">Suspendido</option>
          <option value="archived">Archivado</option>
        </select>
      </div>

      <Card className="overflow-hidden">
        {query.isPending ? (
          <CenteredSpinner label="Cargando equipos…" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : (query.data?.teams ?? []).length === 0 ? (
          <EmptyState
            title={search ? "Sin resultados" : "No hay equipos"}
            description={search ? "Intenta con otro término de búsqueda." : "No se encontraron equipos."}
          />
        ) : (
          <>
            <Table>
              <THead>
                <tr>
                  <TH>Equipo</TH>
                  <TH>Teléfono owner</TH>
                  <TH>Miembros</TH>
                  <TH>Empresas</TH>
                  <TH>Estado</TH>
                  <TH>Suscripción</TH>
                  <TH>Creado</TH>
                  <TH>Acciones</TH>
                </tr>
              </THead>
              <TBody>
                {query.data.teams.map((t) => (
                  <TR key={t.id} className="cursor-pointer" onClick={() => navigate(`/teams/${t.id}`)}>
                    <TD>
                      <p className="font-medium text-slate-900">{t.name}</p>
                      <p className="text-[11px] text-slate-500 font-mono">{t.slug}</p>
                    </TD>
                    <TD className="font-mono text-xs">
                      {t.ownerPhone ? maskPhone(t.ownerPhone) : "—"}
                    </TD>
                    <TD>{t.memberCount}</TD>
                    <TD>{t.companyCount}</TD>
                    <TD>
                      <StatusBadge status={t.status} />
                    </TD>
                    <TD>
                      {t.subscriptionStatus ? (
                        <span className="text-xs">{t.subscriptionStatus}</span>
                      ) : (
                        "—"
                      )}
                    </TD>
                    <TD className="text-[11px] text-slate-500">{formatDate(t.createdAt)}</TD>
                    <TD onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {t.status === "active" ? (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => setSuspendTarget({ id: t.id, name: t.name })}
                            loading={suspendMutation.isPending && suspendTarget?.id === t.id}
                          >
                            Suspender
                          </Button>
                        ) : t.status === "suspended" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onReactivate(t.id, t.name)}
                            loading={reactivateMutation.isPending}
                          >
                            Reactivar
                          </Button>
                        ) : null}
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
              <p className="text-xs text-slate-600">
                {total} total · página {page} de {pageCount}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Anterior
                </Button>
                <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)}>
                  Siguiente
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Suspend dialog */}
      <Dialog
        open={!!suspendTarget}
        onClose={() => { setSuspendTarget(null); setSuspendReason(""); }}
        title={`Suspender equipo: ${suspendTarget?.name}`}
        description="El equipo quedará suspendido y sus miembros no podrán acceder a la plataforma."
      >
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-700">Motivo</label>
            <textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              rows={3}
              placeholder="Describe el motivo de la suspensión…"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => { setSuspendTarget(null); setSuspendReason(""); }}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={onSuspend}
              loading={suspendMutation.isPending}
              disabled={!suspendReason.trim()}
            >
              Confirmar suspensión
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={closeCreateDialog}
        title="Crear equipo"
        description="Crea un equipo a nombre de un cliente de la plataforma."
      >
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-700">Nombre del equipo</label>
            <Input
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              className="mt-1"
              placeholder="Contadores Asociados SAS"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Celular del dueño</label>
            <Input
              type="tel"
              value={createPhone}
              onChange={(e) => setCreatePhone(e.target.value)}
              className="mt-1"
              placeholder="3001234567"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              El cliente quedará como owner del equipo cuando inicie sesión por primera vez con este celular.
            </p>
          </div>
          {createError && <p className="text-xs text-red-600">{createError}</p>}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={closeCreateDialog}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={onCreate}
              loading={createMutation.isPending}
              disabled={!createName.trim() || !createPhone.trim()}
            >
              Crear equipo
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
